import { useQuery } from '@tanstack/react-query';

import { authApi } from '@/services/auth';
import { useAuth } from '@/store/useAuth';

export const featuresKey = ['auth', 'me'] as const;

/** Mesma identidade, nova pergunta: o plano muda raramente, então 5 minutos bastam. */
const FRESCOR_MS = 5 * 60_000;

/**
 * As features do plano do cliente, lidas de `/auth/me`.
 *
 * Só consulta com sessão (identidade persistida ou token em memória): sem isso a
 * tela de login pediria `/auth/me` e levaria 401. O resultado é cacheado, então
 * dez componentes chamando `useFeature` fazem UMA requisição.
 */
export function useFeatures() {
  const token = useAuth((s) => s.token);
  const username = useAuth((s) => s.username);
  return useQuery({
    queryKey: featuresKey,
    queryFn: authApi.me,
    enabled: Boolean(token || username),
    staleTime: FRESCOR_MS,
    retry: false,
  });
}

export interface EstadoDaFeature {
  /** A feature está no plano? Falso enquanto carrega e quando o servidor não diz. */
  habilitada: boolean;
  /** Ainda não sabemos: a tela decide se espera antes de dizer "não incluído". */
  carregando: boolean;
}

/**
 * Fecha por padrão: servidor antigo (sem `features`), conta sem acesso
 * operacional (`features` vazio) ou falha de rede viram "não habilitada". Quem
 * barra de verdade é a rota (403); aqui só se decide o que mostrar.
 */
export function useFeatureEstado(nome: string): EstadoDaFeature {
  const { data, isPending, isFetching } = useFeatures();
  return {
    habilitada: data?.features?.[nome] === true,
    carregando: isPending && isFetching,
  };
}

/** `useFeature('portal_kit')`: o plano do cliente inclui esta capacidade? */
export function useFeature(nome: string): boolean {
  return useFeatureEstado(nome).habilitada;
}
